/**
 * LabelsAppliedOverlay Component
 *
 * Confirmation overlay displayed on a case card after labels have been applied.
 * Shows a checkmark icon, "Labels Applied" text, applied label chips, and an Undo button.
 *
 * @example
 * ```tsx
 * <LabelsAppliedOverlay
 *   appliedLabels={['Spam', 'Harassment']}
 *   onUndo={() => session.undoLabels(caseId)}
 * />
 * ```
 */

import { Button, ButtonType, Icon, IconSize, Completed, Tag, TagSize, TagVariant, Text } from '@soteria/design-system-ve';
import type { ReactElement } from 'react';

import * as styles from './LabelsAppliedOverlay.css';

export interface LabelsAppliedOverlayProps {
  /**
   * List of applied label names to display as chips
   */
  appliedLabels: string[];

  /**
   * Callback when the Undo button is clicked
   */
  onUndo: () => void;
}

/**
 * LabelsAppliedOverlay — visual confirmation that labels have been applied to a case card.
 *
 * Renders as an absolute-positioned overlay covering the card content with:
 * - XDS Completed (checkmark) icon
 * - "Labels Applied" heading
 * - Applied label chips
 * - Undo button to revert the action
 *
 * @accessibility
 * - `role="status"` for screen reader announcement
 * - `aria-live="polite"` ensures overlay change is announced
 * - Undo button is keyboard accessible
 */
export function LabelsAppliedOverlay({ appliedLabels, onUndo }: LabelsAppliedOverlayProps): ReactElement {
  return (
    <div className={styles.overlayContainer} role="status">
      <div className={styles.overlayContent}>
        {/* Checkmark icon */}
        <div aria-hidden="true" className={styles.checkmarkIcon}>
          <Icon icon={Completed} iconSize={IconSize.Small} />
        </div>

        {/* Title */}
        <Text className={styles.overlayTitle}>Labels Applied</Text>

        {/* Applied label chips */}
        {appliedLabels.length > 0 && (
          <div className={styles.labelChips}>
            {appliedLabels.map(label => (
              <Tag key={label} label={label} maxWidth="160px" size={TagSize.Small} variant={TagVariant.Default} />
            ))}
          </div>
        )}

        {/* Undo button */}
        <div className={styles.undoButton}>
          <Button aria-label="Undo applied labels" buttonType={ButtonType.Secondary} onClick={onUndo}>
            Undo
          </Button>
        </div>
      </div>
    </div>
  );
}
